import { useCallback, useMemo, useState } from 'react';
import type { Transaction } from '@/api/rpc/hooks/ship-and-debit/useE844sdTransactions';
import { SD_TRANSACTION_STATUS } from '@/api/rpc/hooks/ship-and-debit/useE844sdTransactions';
import {
  useStagedStats,
  useListTransactions,
  useStageTransactions,
  useUnstageTransactions,
  useUnpostTransactions,
  useUpdateStagedTransaction,
} from '@/api/rpc/hooks/ship-and-debit';

export interface WorkbenchTransaction extends Transaction {
  /** If set, this transaction failed to send (client-side only) */
  failureReason?: string;
  /** Timestamp when the transaction failed (client-side only) */
  failedAt?: number;
}

/** Optional callbacks for workbench mutations. Lets modal action handlers
 *  gate UI advancement on mutation success — failures keep the modal on
 *  the row instead of optimistically navigating away. */
export interface MutationCallbacks {
  onSuccess?: () => void;
  onError?: (err: unknown) => void;
}

export function useWorkbench(
  params: {
    limit?: number;
    offset?: number;
    columnFilters?: Record<string, string[]>;
    textFilters?: Record<string, string[]>;
    dateFilters?: Record<string, string[]>;
    numericFilters?: Record<string, string[]>;
    hasComments?: boolean;
    dunsNotOnSpa?: boolean;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
    transactionIds?: string[];
  } = {},
) {
  const {
    transactions: stagedTransactions,
    isLoading,
    isFetching,
    pagination,
  } = useListTransactions({
    // Posted rows are `staged` + posted_at, so STAGED returns staged + posted.
    statuses: [SD_TRANSACTION_STATUS.STAGED],
    limit: params.limit ?? 1000,
    offset: params.offset ?? 0,
    columnFilters: params.columnFilters,
    textFilters: params.textFilters,
    dateFilters: params.dateFilters,
    numericFilters: params.numericFilters,
    hasComments: params.hasComments,
    dunsNotOnSpa: params.dunsNotOnSpa,
    sortBy: params.sortBy,
    sortDirection: params.sortDirection,
    transactionIds: params.transactionIds,
  });
  const { data: stagedStats } = useStagedStats();
  const stageMutation = useStageTransactions();
  const unstageMutation = useUnstageTransactions();
  const unpostMutation = useUnpostTransactions();
  const updateMutation = useUpdateStagedTransaction();

  // Client-side failure tracking (transient send-session data)
  const [failureMap, setFailureMap] = useState<
    Map<string, { reason: string; at: number }>
  >(new Map());

  // Merge backend staged transactions with client-side failure state
  const transactions = useMemo<WorkbenchTransaction[]>(
    () =>
      stagedTransactions.map((t) => {
        const failure = failureMap.get(t.id);
        return {
          ...t,
          failureReason: failure?.reason,
          failedAt: failure?.at,
        };
      }),
    [stagedTransactions, failureMap],
  );

  const workbenchIds = useMemo(
    () => new Set(transactions.map((t) => t.id)),
    [transactions],
  );

  const stageTransactions = stageMutation.mutate;
  const unstageTransactions = unstageMutation.mutate;
  const unpostTransactions = unpostMutation.mutate;

  const addToWorkbench = useCallback(
    (
      newTransactions: Transaction | Transaction[],
      options?: MutationCallbacks,
    ) => {
      const toAdd = Array.isArray(newTransactions)
        ? newTransactions
        : [newTransactions];
      const ids = toAdd.map((t) => t.id);
      if (ids.length > 0) {
        stageTransactions(
          { transactionIds: ids, transactions: toAdd },
          {
            onSuccess: options?.onSuccess,
            onError: options?.onError,
          },
        );
      }
    },
    [stageTransactions],
  );

  const removeFromWorkbench = useCallback(
    (transactionIds: string | string[], options?: MutationCallbacks) => {
      const ids = Array.isArray(transactionIds)
        ? transactionIds
        : [transactionIds];
      if (ids.length > 0) {
        unstageTransactions(ids, {
          onSuccess: options?.onSuccess,
          onError: options?.onError,
        });
      }
    },
    [unstageTransactions],
  );

  const unpostFromWorkbench = useCallback(
    (transactionIds: string | string[], options?: MutationCallbacks) => {
      const ids = Array.isArray(transactionIds)
        ? transactionIds
        : [transactionIds];
      if (ids.length > 0) {
        unpostTransactions(ids, {
          onSuccess: options?.onSuccess,
          onError: options?.onError,
        });
      }
    },
    [unpostTransactions],
  );

  const updateTransaction = useCallback(
    (transactionId: string, updates: Partial<Transaction>) => {
      const numericUpdates: Record<string, number> = {};

      for (const [field, val] of Object.entries(updates)) {
        if (val != null && typeof val === 'number') {
          numericUpdates[field] = val;
        }
      }

      if (Object.keys(numericUpdates).length > 0) {
        updateMutation.mutate({ transactionId, updates: numericUpdates });
      }
    },
    [updateMutation],
  );

  const markTransactionsAsFailed = useCallback(
    (failures: Array<{ id: string; reason: string }>) => {
      setFailureMap((prev) => {
        const next = new Map(prev);
        for (const f of failures) {
          next.set(f.id, { reason: f.reason, at: Date.now() });
        }
        return next;
      });
    },
    [],
  );

  const clearFailureStatus = useCallback(
    (transactionIds?: string | string[]) => {
      setFailureMap((prev) => {
        if (!transactionIds) return new Map();
        const idsToClear = new Set(
          Array.isArray(transactionIds) ? transactionIds : [transactionIds],
        );
        const next = new Map(prev);
        for (const id of idsToClear) {
          next.delete(id);
        }
        return next;
      });
    },
    [],
  );

  const clearWorkbench = useCallback(() => {
    unstageTransactions({ all: true });
    setFailureMap(new Map());
  }, [unstageTransactions]);

  const getTransaction = useCallback(
    (transactionId: string) => {
      return transactions.find((t) => t.id === transactionId);
    },
    [transactions],
  );

  const isInWorkbench = useCallback(
    (transactionId: string) => {
      return workbenchIds.has(transactionId);
    },
    [workbenchIds],
  );

  const totalClaimAmount = stagedStats?.totalDebitAmount ?? 0;

  const transactionCount = transactions.length;

  const failedCount = useMemo(
    () => transactions.filter((t) => t.failureReason).length,
    [transactions],
  );

  const failedTransactions = useMemo(
    () => transactions.filter((t) => t.failureReason),
    [transactions],
  );

  return {
    transactions,
    workbenchIds,
    isLoading,
    isFetching,
    addToWorkbench,
    /** True while a stage mutation is in flight — modal action handlers
     *  read this to prevent double-firing and skipping the next transaction. */
    isAddingToWorkbench: stageMutation.isPending,
    removeFromWorkbench,
    /** True while an unstage mutation is in flight. */
    isRemovingFromWorkbench: unstageMutation.isPending,
    /** Un-post posted transactions (reverse posting → editable staged). */
    unpostFromWorkbench,
    /** True while an un-post mutation is in flight. */
    isUnposting: unpostMutation.isPending,
    updateTransaction,
    markTransactionsAsFailed,
    clearFailureStatus,
    clearWorkbench,
    getTransaction,
    isInWorkbench,
    pagination,
    totalClaimAmount,
    transactionCount,
    failedCount,
    failedTransactions,
  };
}
